import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PublishIcon from "@mui/icons-material/Publish";
import {
	Box,
	Button,
	Chip,
	FormControl,
	Grid2,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	CellValueChangedEvent,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import {
	TestsBySection,
	TestsBySectionVersion,
} from "../../../models/TestsBySection";
import {
	createTestsBySectionVersion,
	deleteTestsBySection,
	deleteTestsBySectionVersion,
	getTestsBySection,
	getTestsBySectionVersions,
	publishTestsBySectionVersion,
	upsertTestsBySection,
} from "../../../repositories/TestsBySectionRepository";
import { useTableData } from "../hooks/useTableData";
import { useTestsBySectionOptionsFunctions } from "../hooks/useTestsBySectionOptionsFunctions";
import { AddRowModal, RowPayload } from "./AddRowModal";
import { CheckboxCellContext } from "./CheckboxCell";
import { NewVersionModal } from "./NewVersionModal";

export const applyToggle = (
	row: TestsBySection,
	field: keyof TestsBySection,
): TestsBySection => {
	const next: TestsBySection = { ...row, [field]: !row[field] };

	const isPull = field === "Pull2" || field === "Pull8" || field === "Pull505";
	if (isPull && !row[field]) {
		next.Pull2 = field === "Pull2";
		next.Pull8 = field === "Pull8";
		next.Pull505 = field === "Pull505";
	}

	return next;
};

export const useTestsBySectionVersions = () =>
	useQuery<TestsBySectionVersion[], Error>({
		queryKey: ["testsBySectionVersions"],
		queryFn: ({ signal }) => getTestsBySectionVersions(signal),
	});

export const useTestsBySection = (versionId: number | null) =>
	useQuery<TestsBySection[], Error>({
		queryKey: ["testsBySection", versionId],
		queryFn: ({ signal }) => getTestsBySection({ versionId }, signal),
		enabled: versionId != null,
	});

type Props = {
	readOnly?: boolean;
};

export const TestsBySectionTable = ({ readOnly = false }: Props) => {
	const queryClient = useQueryClient();

	const [filterValue, setFilterValue] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
		null,
	);
	const [addRowOpen, setAddRowOpen] = useState(false);
	const [newVersionOpen, setNewVersionOpen] = useState(false);
	const [editRow, setEditRow] = useState<TestsBySection | null>(null);
	const [confirm, setConfirm] = useState<"publish" | "discard" | null>(null);

	const {
		data: allVersions,
		isLoading: versionsLoading,
		error: versionsError,
		isError: isVersionsError,
	} = useTestsBySectionVersions();

	const versions = readOnly
		? allVersions?.filter((v) => v.Status === "Published")
		: allVersions;

	const defaultVersionId =
		versions?.find((v) => v.Status === "Published")?.TestsBySectionVersionId ??
		versions?.[0]?.TestsBySectionVersionId ??
		null;

	const versionId = selectedVersionId ?? defaultVersionId;

	const {
		data: tests,
		isLoading: testsLoading,
		error: testsError,
		isError: isTestsError,
	} = useTestsBySection(versionId);

	const selectedVersion =
		versions?.find((v) => v.TestsBySectionVersionId === versionId) ?? null;
	const isEditMode = !readOnly && selectedVersion?.Status === "Draft";
	const maxRev = (versions ?? []).reduce((max, v) => Math.max(max, v.Rev), 0);

	const upsertMutation = useMutation({
		mutationFn: upsertTestsBySection,
		onSuccess: async () =>
			await queryClient.invalidateQueries({
				queryKey: ["testsBySection", versionId],
			}),
		onError: (e: Error) => setError(e.message),
	});

	const deleteMutation = useMutation({
		mutationFn: deleteTestsBySection,
		onSuccess: async () =>
			await queryClient.invalidateQueries({
				queryKey: ["testsBySection", versionId],
			}),
		onError: (e: Error) => setError(e.message),
	});

	const createVersionMutation = useMutation({
		mutationFn: createTestsBySectionVersion,
		onSuccess: async (newId: number) => {
			await queryClient.invalidateQueries({
				queryKey: ["testsBySectionVersions"],
			});
			setSelectedVersionId(newId);
			setNewVersionOpen(false);
			setMessage("Draft created — edit the tests, then Publish when ready.");
		},
		onError: (e: Error) => setError(e.message),
	});

	const publishMutation = useMutation({
		mutationFn: publishTestsBySectionVersion,
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["testsBySectionVersions"],
			});
			setMessage("Version published.");
		},
		onError: (e: Error) => setError(e.message),
	});

	const discardMutation = useMutation({
		mutationFn: deleteTestsBySectionVersion,
		onSuccess: async () => {
			setSelectedVersionId(null);
			await queryClient.invalidateQueries({
				queryKey: ["testsBySectionVersions"],
			});
			setMessage("Draft discarded.");
		},
		onError: (e: Error) => setError(e.message),
	});

	const isSubmitting = upsertMutation.isPending || deleteMutation.isPending;

	const saveRow = (row: TestsBySection) => {
		if (versionId == null) return;
		upsertMutation.mutate({ testsBySection: row, versionId });
	};

	const handleSaveRow = (payload: RowPayload) => {
		if (versionId == null) return;
		upsertMutation.mutate(
			{ testsBySection: payload as TestsBySection, versionId },
			{
				onSuccess: () => {
					setAddRowOpen(false);
					setEditRow(null);
				},
			},
		);
	};

	const handleEditRow = (row: TestsBySection) => {
		setEditRow(row);
		setAddRowOpen(true);
	};

	const handleDelete = (row: TestsBySection) => {
		if (row.TestsBySectionId == null) return;
		deleteMutation.mutate({ testsBySectionId: row.TestsBySectionId });
	};

	const handleConfirmClose = (value?: string) => {
		const action = confirm;
		setConfirm(null);
		if (value !== "OK" || versionId == null) return;
		if (action === "publish") publishMutation.mutate({ versionId });
		if (action === "discard") discardMutation.mutate({ versionId });
	};

	const onCellValueChanged = (event: CellValueChangedEvent<TestsBySection>) => {
		if (event.colDef.field !== "Comments") return;
		if (event.oldValue === event.newValue) return;
		saveRow(event.data);
	};

	const { columnDefs } = useTableData({ isEditMode });
	const { defaultColDef } = useGridDefinitions({});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useTestsBySectionOptionsFunctions({
		onEdit: handleEditRow,
		onDelete: handleDelete,
	});

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			if (isEditMode && data) {
				showContextMenu(event as any, data, "testsBySectionContext");
			}
			return [];
		},
		[isEditMode],
	);

	useEffect(() => {
		registerConfig({
			id: "testsBySectionContext",
			getOptions: getMenuOptions,
		});
	}, []);

	useEffect(() => {
		if (isVersionsError) {
			setError(versionsError.message);
		}
		if (isTestsError) {
			setError(testsError.message);
		}
	}, [versionsError, isVersionsError, testsError, isTestsError]);

	return (
		<>
			<Grid2 container justifyContent="flex-start" p={1} spacing={1}>
				{!readOnly && (
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Typography variant="h5" fontWeight={600}>
							Tests by Section
						</Typography>
					</Grid2>
				)}

				<Grid2
					container
					size={{ md: 12 }}
					spacing={1}
					alignItems="center"
					justifyContent="space-between"
				>
					<Grid2 container spacing={1} alignItems="center">
						<FormControl size="small" sx={{ minWidth: 240 }}>
							<InputLabel id="tbs-version-label">Version</InputLabel>
							<Select
								labelId="tbs-version-label"
								label="Version"
								value={versionId ?? ""}
								disabled={versionsLoading}
								onChange={(e) => setSelectedVersionId(Number(e.target.value))}
								sx={{
									backgroundColor: (theme) => theme.palette.background.paper,
								}}
							>
								{versions?.map((v) => (
									<MenuItem
										key={v.TestsBySectionVersionId}
										value={v.TestsBySectionVersionId}
									>
										Rev {v.Rev} -{" "}
										{new Date(v.EffectiveDate).toLocaleDateString()}
										{v.Status === "Draft" ? " (Draft)" : ""}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						{isEditMode && <Chip label="Draft" color="warning" size="small" />}

						{selectedVersion?.Comments && (
							<Typography variant="body2" color="text.secondary">
								{selectedVersion.Comments}
							</Typography>
						)}
					</Grid2>

					<Box sx={{ display: "flex", gap: 1 }}>
						{readOnly ? null : isEditMode ? (
							<>
								<Button
									variant="outlined"
									size="small"
									startIcon={<AddIcon />}
									onClick={() => {
										setEditRow(null);
										setAddRowOpen(true);
									}}
								>
									Add Row
								</Button>
								<Button
									variant="outlined"
									size="small"
									color="error"
									startIcon={<DeleteOutlineIcon />}
									onClick={() => setConfirm("discard")}
									disabled={discardMutation.isPending}
								>
									Discard
								</Button>
								<Button
									variant="contained"
									size="small"
									startIcon={<PublishIcon />}
									onClick={() => setConfirm("publish")}
									disabled={publishMutation.isPending || isSubmitting}
								>
									Publish
								</Button>
							</>
						) : (
							<Button
								variant="contained"
								size="small"
								startIcon={<EditIcon />}
								onClick={() => setNewVersionOpen(true)}
								disabled={createVersionMutation.isPending}
							>
								New Version
							</Button>
						)}
					</Box>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 12 }}>
					<TextFieldSearchFilter
						value={filterValue}
						onChange={(val) => {
							setFilterValue(val);
							gridApi?.setGridOption("quickFilterText", val);
						}}
					/>
				</Grid2>

				<Grid2 sx={{ width: "100%", height: "70vh" }}>
					<Paper elevation={2} style={{ height: "100%", width: "100%" }}>
						<AgGridReact<TestsBySection>
							loading={testsLoading}
							rowData={tests}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							context={
								{
									onToggle: (row, field) => saveRow(applyToggle(row, field)),
									onEdit: handleEditRow,
									onDelete: handleDelete,
									isEditMode,
									isSubmitLoading: isSubmitting,
								} satisfies CheckboxCellContext
							}
							rowHeight={34}
							headerHeight={34}
							animateRows
							singleClickEdit
							stopEditingWhenCellsLoseFocus
							getContextMenuItems={getContextMenuItems}
							onGridReady={(params) => setGridApi(params.api)}
							getRowId={(params) =>
								`${params.data.SectionId}-${params.data.GradeId ?? ""}`
							}
							loadingOverlayComponent={CenteredLazyLoading}
							onCellValueChanged={onCellValueChanged}
						/>
					</Paper>
				</Grid2>
			</Grid2>

			<NewVersionModal
				open={newVersionOpen}
				maxRev={maxRev}
				isSubmitting={createVersionMutation.isPending}
				onClose={() => setNewVersionOpen(false)}
				onSubmit={({ rev, effectiveDate, comments }) =>
					createVersionMutation.mutate({
						fromVersionId: versionId,
						rev,
						effectiveDate,
						comments,
					})
				}
			/>

			<AddRowModal
				open={addRowOpen}
				selectedRow={editRow}
				existingRows={tests ?? []}
				isSubmitting={upsertMutation.isPending}
				onClose={() => {
					setAddRowOpen(false);
					setEditRow(null);
				}}
				onSubmit={handleSaveRow}
			/>

			<ConfirmationDialogRaw
				open={confirm !== null}
				title={confirm === "publish" ? "Publish Version" : "Discard Draft"}
				message={
					confirm === "publish"
						? "Publish this version? It will become the active one shown to everyone."
						: "Discard this draft version? This cannot be undone."
				}
				buttons={{
					no: "Cancel",
					yes: confirm === "publish" ? "Publish" : "Discard",
				}}
				onClose={handleConfirmClose}
			/>

			<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
